'use client'

/**
 * Stage 39 — the evidence table shown under one reason, on BOTH anomaly drawers.
 *
 * WHY IT EXISTS
 * The quantitative reasons were correct but long: every number lived inside the sentence,
 * so a reader had to parse prose to find the baseline. Now the sentence states the
 * conclusion and the numbers sit under it in a two-column table — the same component for
 * scenarios 1–5 and 6–7, so the two surfaces cannot drift into different shapes.
 *
 * RULES IT ENFORCES
 *  • a row whose value is absent is not rendered (`pushEvidence` filters at build time,
 *    and this component filters again so a hand-built row cannot slip through);
 *  • fewer than `MIN_EVIDENCE_ROWS` rows → NO table at all: one number without a
 *    comparison is not evidence;
 *  • no horizontal scroll at any width — the table is `table-fixed` and both columns wrap.
 *    Long values break inside the cell instead of widening the drawer.
 *
 * It computes nothing. Every value arrives as a finished string from the explanation
 * builders, which are the only place a number is formatted.
 */
import { useState, type ReactNode } from 'react'
import { useT, useFormat } from '@/lib/i18n'
import {
  MIN_EVIDENCE_ROWS,
  drilldownButtonLabel,
  isMeaningfulValue,
  type EvidenceDrilldown,
  type EvidenceRow,
} from '../evidence-row'

export function AnomalyEvidenceTable({
  rows,
  note,
  compact,
  renderExamples,
  nounKey = 'analytics.evi.nounTransportsAcc',
}: {
  rows: EvidenceRow[]
  /** One short line under the table — window semantics, never a second finding. */
  note?: string | null
  /** Tighter padding for dense contexts; the layout rules are unchanged. */
  compact?: boolean
  /**
   * Stage 40 — renders the rows behind ONE number, mounted only after the analyst opens
   * it. The table itself never fetches: it hands the descriptor back and lays out whatever
   * the caller returns, so the drawer owns the query and its cache.
   */
  renderExamples?: (d: EvidenceDrilldown) => ReactNode
  /** Noun for the «Показать N …» button, declined in the caller's vocabulary. */
  nounKey?: string
}) {
  const shown = (rows ?? []).filter((r) => r && r.label && isMeaningfulValue(r.value))
  if (shown.length < MIN_EVIDENCE_ROWS) return null

  return (
    <div data-block="evidence-table" className={compact ? 'mt-1.5' : 'mt-2'}>
      <table className="w-full table-fixed border-collapse text-[11px]">
        <tbody>
          {shown.map((r) => (
            <EvidenceTableRow
              key={r.label}
              row={r}
              compact={compact}
              nounKey={nounKey}
              renderExamples={renderExamples}
            />
          ))}
        </tbody>
      </table>
      {note && <p className="mt-1 text-[10px] leading-relaxed text-[var(--text-tertiary)]">{note}</p>}
    </div>
  )
}

/**
 * One row, plus — when the number is a count over a concrete set — a disclosure that
 * mounts the examples underneath it.
 *
 * The button is rendered only when the caller supplied a renderer AND the row carries a
 * drill-down with a non-zero total: a count of 0 has nothing to open, and saying so in
 * words is clearer than an empty list.
 */
function EvidenceTableRow({
  row,
  compact,
  nounKey,
  renderExamples,
}: {
  row: EvidenceRow
  compact?: boolean
  nounKey: string
  renderExamples?: (d: EvidenceDrilldown) => ReactNode
}) {
  const t = useT()
  const f = useFormat()
  const [open, setOpen] = useState(false)
  const d = row.drilldown
  const canOpen = !!renderExamples && !!d && d.totalCount > 0
  const cell = compact ? 'py-0.5' : 'py-1'
  // Набор ВАРИАНТОВ — это не список связок: «Показать 7 связок» под строкой
  // «Вариантов комбинации кодов груза — 7» называет не то, что откроется. Склонение
  // берётся из dataset_type — того же ключа, по которому сервис собирает набор.
  const nouns: string = d?.datasetType?.endsWith('_variants')
    ? 'analytics.evi.nounVariants'
    : nounKey

  return (
    <>
      <tr data-evidence-row={row.label} className="align-top">
        <th
          scope="row"
          className={`w-[58%] break-words border-b border-[var(--border-subtle)] pr-2 text-left font-normal text-[var(--text-tertiary)] ${cell}`}
        >
          {/* Подсказка висит и на подписи, а не только на числе: объясняется состав
              показателя («что именно считается»), и искать её у значения читателю
              незачем. Пунктир — единственный видимый признак, что подсказка есть. */}
          {row.tooltip ? (
            <span
              data-evidence-tooltip="1"
              title={row.tooltip}
              className="cursor-help underline decoration-dotted underline-offset-2"
            >
              {row.label}
            </span>
          ) : (
            row.label
          )}
        </th>
        <td
          title={row.tooltip ?? undefined}
          className={`break-words border-b border-[var(--border-subtle)] text-right tabular-nums text-[var(--text-primary)] ${cell}`}
        >
          {row.value}
        </td>
      </tr>
      {canOpen && (
        <tr data-evidence-drilldown={d!.evidenceKey}>
          <td colSpan={2} className="border-b border-[var(--border-subtle)] pb-1">
            {/* Stage 41: the toggle sits on the right, as it does on the reference
                «Похожие кейсы в периоде» panel, so the eye finds it in the same place
                whether the set is open or closed. */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="text-right text-[10px] text-[var(--accent-data)] underline decoration-dotted hover:text-[var(--text-primary)]"
              >
                {open ? t('analytics.evidence.hideExamples') : `▸ ${drilldownButtonLabel(d!, nouns, t, f)}`}
              </button>
            </div>
            {/* Mounted only after the click — the drawer's first render fetches nothing. */}
            {open && <div className="mt-1">{renderExamples!(d!)}</div>}
          </td>
        </tr>
      )}
    </>
  )
}

export { MIN_EVIDENCE_ROWS }
export type { EvidenceRow }
